import { useState } from "react";
import { Eye, Ban, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePoll } from "@/hooks/usePoll";
import { api, errMsg, rp, fmtDateTime, todayStr, METHOD_LABEL } from "@/lib/api";
import { PageHeader, Panel, Empty, StageBadge } from "@/components/common";
import BillDetailDialog from "@/components/BillDetailDialog";

export default function Transactions({ mode }) {
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const params = mode === "paid" ? { status: "paid", date_from: from, date_to: to } : { status: "active" };
  const { data, reload } = usePoll("/bills", mode === "active" ? 5000 : 0, params);
  const [detail, setDetail] = useState(null);
  const rows = data || [];

  const cancel = async (b) => {
    if (!window.confirm(`Batalkan bill ${b.code}?`)) return;
    try {
      await api.post(`/bills/${b.id}/cancel`);
      toast.success("Bill dibatalkan");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader title={mode === "paid" ? "Riwayat Transaksi" : "Transaksi Berjalan"} subtitle={mode === "paid" ? "Transaksi yang sudah lunas" : "Bill yang masih aktif, diperbarui otomatis"}>
        {mode === "paid" && (
          <>
            <Input data-testid="history-date-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 bg-white" />
            <span className="text-sm text-zinc-500">s/d</span>
            <Input data-testid="history-date-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 bg-white" />
          </>
        )}
      </PageHeader>
      <Panel className="overflow-x-auto">
        {rows.length === 0 ? <Empty icon={Receipt} text="Tidak ada transaksi" testId="transactions-empty" /> : (
          <Table data-testid="transactions-table">
            <TableHeader><TableRow>
              <TableHead>No. Bill</TableHead><TableHead>Pelanggan</TableHead><TableHead>Meja</TableHead><TableHead>Status</TableHead>
              {mode === "paid" && <TableHead>Metode</TableHead>}
              <TableHead className="text-right">Total</TableHead><TableHead>{mode === "paid" ? "Dibayar" : "Dibuka"}</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((b) => (
                <TableRow key={b.id} data-testid={`tx-row-${b.code}`} className="hover:bg-orange-50/50">
                  <TableCell className="font-mono font-semibold">{b.code}</TableCell>
                  <TableCell>{b.customer_name}</TableCell>
                  <TableCell>{b.table_name}</TableCell>
                  <TableCell><StageBadge stage={b.stage} /></TableCell>
                  {mode === "paid" && <TableCell>{METHOD_LABEL[b.payment?.method]}</TableCell>}
                  <TableCell className="text-right font-mono">{rp(b.totals.total)}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-zinc-500">{fmtDateTime(mode === "paid" ? b.paid_at : b.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" data-testid={`tx-detail-${b.code}`} onClick={() => setDetail(b)}><Eye className="mr-1 h-4 w-4" />Rincian</Button>
                      {mode === "active" && <Button size="sm" variant="ghost" data-testid={`tx-cancel-${b.code}`} className="text-red-600" onClick={() => cancel(b)}><Ban className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      {mode === "paid" && rows.length > 0 && (
        <p className="mt-3 text-right text-sm text-zinc-600" data-testid="history-sum">{rows.length} transaksi &middot; Total <span className="font-mono font-bold">{rp(rows.reduce((s, b) => s + b.totals.total, 0))}</span></p>
      )}
      <BillDetailDialog bill={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />
    </div>
  );
}
